/**
 * Well Testing batch 6 — the AI drafts (fieldiq-api#1261, WT-6). Typed from
 * routes/well-testing-ai.routes.ts and lib/wt-ai-digests.ts as merged at
 * 0f6d41b0; the manifest mirror carries the same routes.
 *
 * camelCase reads, `{ success, data }` with no meta. Every run is a HITL draft
 * (agent_runs, outcome 'pending'): `data` is the API's deterministic result,
 * `draft` the model's text (null when it was skipped or rejected — `aiError`
 * says why). Nothing is published until a person approves it.
 */

export const WT_AI_AGENTS = ['wt_shift_summary', 'wt_ticket_preflight', 'wt_test_report_draft', 'wt_compliance_digest'] as const;
export type WtAiAgent = (typeof WT_AI_AGENTS)[number];

export interface WtAiDraft {
  narrative: string;
  observations: string[];
}

// ─── Shift summary ───────────────────────────────────────────────────────────

export interface WtShiftFigure {
  key: string;
  label: string;
  unit: string;
  min: number | null;
  max: number | null;
  first: number | null;
  last: number | null;
}

export interface WtShiftCumulative {
  key: string;
  label: string;
  unit: string;
  start: number | null;
  end: number | null;
  change: number | null;
}

export interface WtShiftSummaryData {
  job: { id: string; jobNumber: string; customer: string; well: string; status: string };
  workDate: string;
  shift: 'day' | 'night' | string;
  window: {
    from: string;
    to: string;
    fromLocal: string;
    toLocal: string;
    timeZone: string;
    shiftStartTime: string;
    shiftLengthHours: number;
    hours: number;
  } | null;
  shiftLogId: string | null;
  crew: Array<{ userId: string; name: string; role: string | null }>;
  crewCount: number | null;
  hours: number | null;
  standbyHours: number | null;
  units: Array<{ label: string; hours: number | null; standbyHours: number | null }>;
  unitHours: number | null;
  safetyNotes: string | null;
  handoverNote: string | null;
  readings: { count: number | null; firstAt: string | null; lastAt: string | null } | null;
  pressures: WtShiftFigure[];
  rates: WtShiftFigure[];
  cumulatives: WtShiftCumulative[];
  loadRecoveredPct: number | null;
  h2sMaxPpm: number | null;
  flags: {
    raised: number | null;
    acknowledged: number | null;
    unacknowledged: number | null;
    missedIntervals: number | null;
    byKind: Array<{ kind: string; label: string; raised: number | null; acknowledged: number | null }>;
    open: Array<{ flagId: string; kind: string; label: string; field: string; at: string }>;
  } | null;
}

// ─── Preflight (one ticket, or one job's open tickets) ───────────────────────

export interface WtPreflightRateGap {
  id: string;
  label: string;
  days: string[];
  reason: string | null;
}

export interface WtPreflightData {
  scope: 'ticket' | 'job';
  job: { id: string; jobNumber: string };
  ticket: { id: string; ticketNumber: string; status: string; scope: 'job' | 'day'; workDate: string | null } | null;
  currency: string | null;
  ticketsChecked: number | null;
  unpricedLines: Array<{ ticketId: string; ticketNumber: string; lineId: string; description: string | null; reason: string | null }>;
  shiftLogsWithoutTicket: Array<{ shiftLogId: string; workDate: string; shift: string }>;
  crewWithoutRate: WtPreflightRateGap[];
  packagesWithoutRate: WtPreflightRateGap[];
  unitsWithoutRate: WtPreflightRateGap[];
  rentInsWithoutRate: WtPreflightRateGap[];
  flagCount: number | null;
  ready: boolean | null;
}

// ─── Test report draft ───────────────────────────────────────────────────────

export interface WtReportDraftData {
  report: { id: string; status: string; title: string | null };
  job: { id: string; jobNumber: string };
  content: unknown;
}

// ─── Compliance digest ───────────────────────────────────────────────────────

export interface WtComplianceItemBase {
  state: string;
  dueOn: string | null;
}

export interface WtComplianceDigestData {
  today: string;
  crewCerts: Array<WtComplianceItemBase & { userId: string; name: string; cert: string; jobNumbers: string[] }>;
  psv: Array<WtComplianceItemBase & { equipmentId: string; label: string; component: string | null; packageName: string | null; jobNumbers: string[] }>;
  calibration: Array<WtComplianceItemBase & { equipmentId: string; label: string; packageName: string | null; jobNumbers: string[]; daysUsed: number | null; dayLimit: number | null }>;
  total: number | null;
}

// ─── A run, as POST returns it and as the register stores it ─────────────────

/** POST /ai/compliance-digest (and the other runs) — 201. */
export interface WtAiRunResult<T = unknown> {
  agentRunId: string;
  agent: WtAiAgent | string;
  status: 'draft' | string;
  data: T | null;
  draft: WtAiDraft | null;
  aiError: string | null;
}

/** GET /ai/digests (≤ 20, newest first) and GET /ai/digests/:id — shapeWtRun. */
export interface WtAiStoredRun<T = unknown> {
  agentRunId: string;
  agent: WtAiAgent | string;
  status: string;
  /** agent_runs.outcome: pending | actioned | dismissed | superseded | no_action. */
  outcome: string | null;
  trigger: string | null;
  triggerData: Record<string, unknown> | null;
  data: T | null;
  draft: WtAiDraft | null;
  approved: WtAiDraft | null;
  approvedEdited: boolean | null;
  aiError: string | null;
  createdAt: string | null;
  reviewedAt: string | null;
}

/** POST /ai/digests/:id/approve — omit both to approve the draft as written. */
export interface WtAiApproveBody {
  narrative?: string;
  observations?: string[];
}
